import React, { useState } from "react";

import { Box, Textarea } from "@chakra-ui/react";
import {
  GoalSavingsStatusEnum,
  IPauseIndividualSavingsPayload,
} from "api/individual-savings/types";
import { pauseIndividualSavings } from "api/individual-user-management";
import { ReactComponent as PauseIcon } from "assets/icons/pause-rounded.svg";
import { useIndividualsSavings } from "contexts/individuals-savings-context";
import { MIN_REASON_LENGTH } from "features/individuals-savings/consts";
import { transactionLimitModalHeaderCss } from "features/individuals/individual-details/styles";
import { useUploadDocument } from "hooks/useUploadDocument";
import { REGEX_VALUES, getUniqueFileName } from "utils";

import { Button } from "ui/button";
import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { FileUpload } from "ui/file-upload";
import { Modal } from "ui/modal";
import { ModalHeader } from "ui/modal/modal-header";
import { defaultModalCss } from "ui/modal/styles";
import { Heading3, Paragraph3 } from "ui/typography";

import { IPauseSavingsModalProps } from "./types";

export const PauseSavingsModal = (props: IPauseSavingsModalProps) => {
  const {
    isOpen,
    setIsOpen,
    handleTogglePauseSavingsModal,
    id,
    type,
    setIsPauseSuccessful,
  } = props;
  const { setIndividualGoalSavingsResult } = useIndividualsSavings();
  const { isUploading, uploadFile } = useUploadDocument();

  const [isPausing, setIsPausing] = useState(false);
  const [formData, setFormData] = useState<{
    reason: string;
    attachment: File | null;
  }>({
    reason: "",
    attachment: null,
  });

  const handleCloseModal = () => {
    setFormData({
      reason: "",
      attachment: null,
    });
    handleTogglePauseSavingsModal();
  };

  const handlePauseSavings = async () => {
    setIsPausing(true);
    const { reason, attachment } = formData;

    try {
      const payload: IPauseIndividualSavingsPayload = {
        id,
        plan: type,
        reason,
      };

      if (attachment) {
        const {
          status,
          url,
          message: singleFileUploadMsg,
        } = await uploadFile({
          file: attachment as File,
          documentName: "others",
          companyName: getUniqueFileName(
            `${id}-pause-${type}-savings-attachment`
          ),
        });
        if (status === "failed") {
          customErrorToast({
            message: singleFileUploadMsg,
          });
          setIsPausing(false);
          return;
        }
        payload["attachment"] = url;
      }

      const { error, message } = await pauseIndividualSavings(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        customSuccessToast({
          message: "Savings Paused Successfully",
        });
        switch (type) {
          case "goal":
            setIndividualGoalSavingsResult((prev) => {
              if (!prev) {
                return prev;
              }
              const updatedGoalSavings = prev.data.map((prevGoalSavings) => {
                if (prevGoalSavings.id !== id) {
                  return prevGoalSavings;
                }
                return {
                  ...prevGoalSavings,
                  status: GoalSavingsStatusEnum.PAUSED,
                };
              });
              return {
                ...prev,
                data: updatedGoalSavings,
              };
            });
            break;
          default:
            break;
        }
        if (setIsPauseSuccessful) {
          setIsPauseSuccessful(true);
        }
        handleCloseModal();
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsPausing(false);
  };

  const handleTextAreaChange = (
    evt: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = evt.target;
    if (!REGEX_VALUES.LETTERS_WITH_NUMBERS_COMMA_AND_HYPHEN_ONLY.test(value)) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (name: string, file: File | null) => {
    setFormData((prev) => ({
      ...prev,
      [name]: file,
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      modalCss={{
        ...defaultModalCss,
        width: "400px",
        padding: "24px",
        gap: "32px",
      }}
      allowOutsideClick={false}
    >
      <Box className="flex flex-col gap-5">
        <ModalHeader
          modalType="others"
          HeaderIcon={<PauseIcon />}
          modalHeaderCss={transactionLimitModalHeaderCss}
        />
        <Box className="flex flex-col gap-2">
          <Heading3
            fontSize="18px"
            lineHeight="28px"
            textColor="#101828"
            fontWeight="600"
          >
            Pause This Goal Savings
          </Heading3>
          <Paragraph3
            lineHeight="20px"
            textColor="#475467"
          >
            Please confirm that you want to perform this action.
          </Paragraph3>
        </Box>
        <Box className="h-[199px] flex flex-col gap-[6px]">
          <label className="text-[#101828] text-sm font-semibold">
            Reason for pause
          </label>
          <Textarea
            css={{
              border: "1px solid #D0D5DD",
              boxShadow: "box-shadow: 0px 1px 2px 0px #1018280D",
              borderRadius: "8px",
              height: "173px",
              padding: "10px 16px",
              fontSize: "14px",
              resize: "none",

              "&::placeholder": {
                color: "#B1B9CA",
              },
            }}
            placeholder="Enter your reason..."
            onChange={handleTextAreaChange}
            value={formData.reason}
            name="reason"
          />
        </Box>
        <Box className="flex flex-col gap-[6px]">
          <FileUpload
            fileUploadLabel="Attachment (optional)"
            file={formData.attachment}
            name="attachment"
            handleFileChange={handleFileChange}
            doesUploadedFileContainerHaveFullWidth
            fileTypes={[
              "jpeg",
              "jpg",
              "png",
              "pdf",
              "doc",
              "docx",
              "xls",
              "xlsx",
              "csv",
            ]}
            uploadFileEmptyStateContainerCss={{
              height: "118px",
              flexShrink: "0",
            }}
            fileUploadLabelCss={{
              fontSize: "14px",
            }}
          />
        </Box>
      </Box>
      <Box
        css={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <Button
          variant="outline"
          textColor="#344054"
          border="1px solid #D0D5DD"
          className="w-[170px]"
          onClick={handleCloseModal}
          disabled={isPausing || isUploading}
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          textColor="#ffffff"
          border="1px solid #00535C"
          className="w-[170px]"
          onClick={handlePauseSavings}
          disabled={
            isPausing ||
            formData.reason.length < MIN_REASON_LENGTH ||
            isUploading
          }
          loading={isPausing || isUploading}
          loadingText="Pausing..."
        >
          Confirm
        </Button>
      </Box>
    </Modal>
  );
};
